import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { supabase, directFetch, directFetchSingle } from '@/lib/supabase';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import StorageImage from '@/components/base/StorageImage';

interface BlogPostFull {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  cover_image: string;
  author: string;
  status: string;
  published_at: string;
  updated_at: string;
  tags: string[] | null;
  seo_title: string;
  seo_description: string;
}

export default function BlogDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostFull | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<BlogPostFull[]>([]);
  const [prevPost, setPrevPost] = useState<BlogPostFull | null>(null);
  const [nextPost, setNextPost] = useState<BlogPostFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchPost = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Check both Supabase auth AND custom admin session for preview mode
      let isAuth = false;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        isAuth = !!session;
      } catch {
        isAuth = false;
      }
      // Also check custom admin session (stored when logging in at /admin/login)
      if (!isAuth) {
        const adminRaw = localStorage.getItem('admin_session');
        if (adminRaw) {
          const ts = parseInt(adminRaw, 10);
          isAuth = !isNaN(ts) && Date.now() - ts < 24 * 60 * 60 * 1000;
        }
      }

      let postData: BlogPostFull | null = null;

      // Primary: direct REST fetch (works around backend gateway issues with the publishable key)
      const directParams: Record<string, string> = {
        select: '*',
        slug: `eq.${slug}`,
      };
      if (!isAuth) {
        directParams.status = 'eq.published';
      }
      const { data: directData, error: directError } = await directFetchSingle('blog_posts', directParams);

      if (!directError && directData) {
        postData = directData as BlogPostFull;
      } else {
        // Fallback: supabase-js client
        let query = supabase.from('blog_posts').select('*').eq('slug', slug);
        if (!isAuth) query = query.eq('status', 'published');
        const { data, error: fetchError } = await query.maybeSingle();
        if (fetchError) {
          const errAny = fetchError as any;
          const msg = errAny.message || errAny.Message || errAny.details || errAny.Details || '';
          setError(`${msg || 'Could not retrieve blog post'}${directError ? ` (direct fetch: ${directError})` : ''}`);
          setLoading(false);
          return;
        }
        postData = (data as BlogPostFull) || null;
      }

      if (!postData) {
        setError('Post not found');
        setLoading(false);
        return;
      }
      setPost(postData);

      // Fetch all published posts for prev/next and related
      const listParams: Record<string, string> = {
        select: 'id,title,slug,excerpt,cover_image,author,published_at,tags',
        status: 'eq.published',
        order: 'published_at.desc',
      };
      const { data: directList, error: listError } = await directFetch('blog_posts', listParams);
      let all = (directList as BlogPostFull[]) || [];
      if (listError || !directList) {
        const { data: allPublished } = await supabase
          .from('blog_posts')
          .select('id, title, slug, excerpt, cover_image, author, published_at, tags')
          .eq('status', 'published')
          .order('published_at', { ascending: false });
        all = (allPublished as BlogPostFull[]) || [];
      }

      const idx = all.findIndex((p) => p.slug === slug);
      setPrevPost(idx > 0 ? all[idx - 1] : null);
      setNextPost(idx < all.length - 1 ? all[idx + 1] : null);
      setRelatedPosts(all.filter((p) => p.slug !== slug).slice(0, 3));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg ? `Unexpected error: ${msg}` : 'Failed to load post');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <div className="flex-1 flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Navbar />
        <div className="flex-1 flex items-center justify-center py-20">
          <div className="text-center">
            <i className="ri-file-search-line text-foreground-300 text-6xl mb-4 block" />
            <h2 className="font-heading text-foreground-900 text-2xl mb-2">Article Not Found</h2>
            <p className="text-foreground-500 font-body text-sm mb-6 max-w-[500px] mx-auto">{error || 'The article you\'re looking for could not be found.'}</p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => fetchPost()}
                className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-6 py-3 rounded-md text-sm font-body font-semibold whitespace-nowrap hover:bg-primary-600 transition-all"
              >
                <i className="ri-refresh-line" />Retry
              </button>
              <Link to="/blog" className="inline-flex items-center gap-2 border-2 border-primary-500 text-primary-500 px-6 py-3 rounded-md text-sm font-body font-semibold whitespace-nowrap hover:bg-primary-50 transition-all">
                <i className="ri-arrow-left-line" />Back to Blog
              </Link>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const readingTime = Math.max(1, Math.ceil((post.content || '').split(/\s+/).length / 200));
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      {/* Breadcrumbs */}
      <div className="bg-background-50 border-b border-background-200/70">
        <div className="max-w-[800px] mx-auto px-4 md:px-6 lg:px-10 py-3 flex items-center justify-between">
          <nav className="flex items-center gap-2 text-xs font-body">
            <Link to="/" className="text-foreground-500 hover:text-foreground-700 transition-colors">Home</Link>
            <span className="text-foreground-300">/</span>
            <Link to="/blog" className="text-foreground-500 hover:text-foreground-700 transition-colors">Blog</Link>
            <span className="text-foreground-300">/</span>
            <span className="text-foreground-700">{post.title.length > 50 ? post.title.slice(0, 50) + '...' : post.title}</span>
          </nav>
          <Link to="/blog" className="inline-flex items-center gap-1.5 text-foreground-600 hover:text-primary-500 font-body text-xs transition-colors whitespace-nowrap group">
            <i className="ri-arrow-left-line group-hover:-translate-x-0.5 transition-transform" />
            Back to Blog
          </Link>
        </div>
      </div>

      <main className="flex-1">
        <article className="py-10 md:py-16">
          <div className="max-w-[800px] mx-auto px-4 md:px-6 lg:px-10">
            {/* Draft badge */}
            {post.status !== 'published' && (
              <div className="mb-4">
                <span className="px-3 py-1 rounded-full text-xs font-body font-medium bg-accent-100 text-accent-700">
                  Preview Mode — {post.status}
                </span>
              </div>
            )}

            {/* Header */}
            <header className="mb-8">
              <div className="flex flex-wrap gap-2 mb-4">
                {(post.tags || []).map((label) => (
                  <span key={label} className="bg-primary-50 text-primary-600 font-body text-[10px] uppercase tracking-wider px-2.5 py-0.5 rounded-full">{label}</span>
                ))}
              </div>
              <h1 className="font-heading text-foreground-900 text-2xl md:text-3xl lg:text-4xl leading-tight mb-4">
                {post.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-foreground-500 font-body text-xs md:text-sm">
                <span>{post.author}</span>
                <span className="text-foreground-300">·</span>
                <span>{formatDate(post.published_at || post.updated_at)}</span>
                <span className="text-foreground-300">·</span>
                <span>{readingTime} min read</span>
              </div>
            </header>

            {/* Cover image */}
            {post.cover_image && (
              <div className="aspect-[16/9] rounded-lg overflow-hidden mb-10">
                <StorageImage
                  path={post.cover_image}
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Content rendered as HTML */}
            <div
              className="prose-content"
              dangerouslySetInnerHTML={{ __html: post.content || '' }}
            />

            {/* Share */}
            <div className="mt-12 pt-8 border-t border-background-200/70">
              <p className="text-foreground-500 font-body text-xs mb-3">Share this article</p>
              <div className="flex items-center gap-3">
                {[
                  { icon: 'ri-facebook-fill', label: 'Facebook' },
                  { icon: 'ri-twitter-x-fill', label: 'Twitter' },
                  { icon: 'ri-linkedin-fill', label: 'LinkedIn' },
                  { icon: 'ri-mail-line', label: 'Email' },
                ].map((s) => (
                  <button key={s.label} className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-500 hover:bg-background-100 hover:text-foreground-700 transition-colors" aria-label={`Share on ${s.label}`}>
                    <i className={s.icon} />
                  </button>
                ))}
              </div>
            </div>

            {/* Prev/Next */}
            <div className="mt-10 pt-8 border-t border-background-200/70">
              <div className="flex flex-col sm:flex-row items-stretch gap-4">
                {prevPost ? (
                  <Link to={`/blog/${prevPost.slug}`} className="flex-1 flex items-center gap-3 p-4 rounded-lg border border-background-200/70 hover:border-primary-200/60 transition-all group">
                    <i className="ri-arrow-left-line text-foreground-400 group-hover:text-primary-500 transition-colors" />
                    <div>
                      <p className="text-foreground-400 font-body text-[10px] uppercase tracking-wider mb-1">Previous</p>
                      <p className="text-foreground-700 font-body text-sm group-hover:text-primary-600 transition-colors line-clamp-1">{prevPost.title}</p>
                    </div>
                  </Link>
                ) : <div className="flex-1" />}
                {nextPost ? (
                  <Link to={`/blog/${nextPost.slug}`} className="flex-1 flex items-center justify-end gap-3 p-4 rounded-lg border border-background-200/70 hover:border-primary-200/60 transition-all group text-right">
                    <div>
                      <p className="text-foreground-400 font-body text-[10px] uppercase tracking-wider mb-1">Next</p>
                      <p className="text-foreground-700 font-body text-sm group-hover:text-primary-600 transition-colors line-clamp-1">{nextPost.title}</p>
                    </div>
                    <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-500 transition-colors" />
                  </Link>
                ) : <div className="flex-1" />}
              </div>
            </div>
          </div>
        </article>

        {/* Related */}
        {relatedPosts.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100/70">
            <div className="max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10">
              <h3 className="font-heading text-foreground-900 text-xl md:text-2xl text-center mb-10">Related Articles</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                {relatedPosts.map((rp) => (
                  <Link key={rp.id} to={`/blog/${rp.slug}`} className="group bg-background-50 border border-background-200/70 rounded-lg overflow-hidden hover:border-primary-200/60 transition-all duration-300">
                    <div className="aspect-[16/10] overflow-hidden">
                      <StorageImage
                        path={rp.cover_image}
                        alt={rp.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h4 className="font-heading text-foreground-900 text-sm mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">{rp.title}</h4>
                      <span className="text-foreground-400 font-body text-xs">{formatDate(rp.published_at)}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Seller CTA */}
        <section className="py-16 md:py-20 bg-foreground-950">
          <div className="max-w-[700px] mx-auto px-4 md:px-6 lg:px-10 text-center">
            <h2 className="font-heading text-accent-100 text-2xl md:text-3xl mb-4">
              Thinking About Selling Your Home?
            </h2>
            <p className="text-background-100/70 font-body text-base mb-8">
              Get clear, personal guidance on your property's potential value and the next steps involved.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/home-valuation" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-8 py-3.5 rounded-md text-base font-body font-semibold whitespace-nowrap hover:bg-primary-600 transition-all">
                <i className="ri-home-4-line text-lg" />Request a Free Home Valuation
              </Link>
              <Link to="/contact" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-accent-300 text-accent-100 px-8 py-3.5 rounded-md text-base font-body font-semibold whitespace-nowrap hover:bg-accent-300/10 transition-all">
                <i className="ri-chat-3-line text-lg" />Speak With Carol
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}